import React, { useState } from 'react';
import { runWhatIf, saveScenario } from '../api';
import {
  Cube, Lightning, Leaf, Fire, Thermometer,
  ArrowRight, SpinnerGap, ChartLineUp, FloppyDisk, CheckCircle
} from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import ScenarioHistory from './ScenarioHistory';

const INDUSTRY_PARAMS = {
  'Manufacturing': [
    { key: 'energy_reduction', label: 'Energy Reduction', unit: '%', min: 0, max: 25, default: 5 },
    { key: 'scrap_target', label: 'Scrap Target', unit: '%', min: 1, max: 10, default: 3.5 },
    { key: 'oee_improvement', label: 'OEE Improvement', unit: '%', min: 0, max: 15, default: 3 },
    { key: 'maintenance_frequency', label: 'Maintenance Freq.', unit: 'days', min: 7, max: 90, default: 30 },
  ],
  'Oil and Gas': [
    { key: 'flare_reduction', label: 'Flare Reduction', unit: '%', min: 0, max: 50, default: 10 },
    { key: 'compressor_efficiency', label: 'Compressor Eff.', unit: '%', min: 80, max: 99, default: 92 },
    { key: 'methane_detection', label: 'Methane Detection', unit: 'ppm', min: 0, max: 100, default: 20 },
    { key: 'fuel_gas_opt', label: 'Fuel Gas Opt.', unit: '%', min: 0, max: 15, default: 5 },
  ],
  'Marine': [
    { key: 'speed_reduction', label: 'Speed Reduction', unit: 'knots', min: 0, max: 5, default: 1 },
    { key: 'bunker_savings', label: 'Bunker Savings', unit: '%', min: 0, max: 15, default: 4 },
    { key: 'shore_power', label: 'Shore Power Usage', unit: '%', min: 0, max: 100, default: 60 },
    { key: 'route_optimization', label: 'Route Opt.', unit: '%', min: 0, max: 10, default: 3 },
  ],
  'Built Environment': [
    { key: 'hvac_efficiency', label: 'HVAC Efficiency', unit: '%', min: 0, max: 20, default: 8 },
    { key: 'occupancy_opt', label: 'Occupancy Opt.', unit: '%', min: 50, max: 100, default: 75 },
    { key: 'precooling_hours', label: 'Pre-cooling Hours', unit: 'hrs', min: 0, max: 6, default: 2 },
    { key: 'peak_demand_shift', label: 'Peak Demand Shift', unit: '%', min: 0, max: 30, default: 10 },
  ],
  'Materials and Construction': [
    { key: 'low_carbon_mix', label: 'Low-Carbon Mix', unit: '%', min: 0, max: 40, default: 15 },
    { key: 'rework_reduction', label: 'Rework Reduction', unit: '%', min: 0, max: 20, default: 5 },
    { key: 'waste_diversion', label: 'Waste Diversion', unit: '%', min: 0, max: 80, default: 40 },
    { key: 'diesel_reduction', label: 'Diesel Reduction', unit: '%', min: 0, max: 25, default: 8 },
  ],
};

const BG_IMAGES = {
  'Manufacturing': 'https://images.unsplash.com/photo-1758304481074-19eacd3e385d?w=1200&q=60',
  'Built Environment': 'https://images.unsplash.com/photo-1759850344200-e4b29ddcc974?w=1200&q=60',
};

export default function DigitalTwinPanel({ industry, sites }) {
  const [selectedSite, setSelectedSite] = useState('');
  const [params, setParams] = useState(() => {
    const p = {};
    (INDUSTRY_PARAMS[industry] || INDUSTRY_PARAMS['Manufacturing']).forEach(param => {
      p[param.key] = param.default;
    });
    return p;
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [question, setQuestion] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [scenarioTitle, setScenarioTitle] = useState('');
  const [historyKey, setHistoryKey] = useState(0);

  const currentParams = INDUSTRY_PARAMS[industry] || INDUSTRY_PARAMS['Manufacturing'];

  React.useEffect(() => {
    const p = {};
    (INDUSTRY_PARAMS[industry] || INDUSTRY_PARAMS['Manufacturing']).forEach(param => {
      p[param.key] = param.default;
    });
    setParams(p);
    setResult(null);
    setSaved(false);
    if (sites?.length) setSelectedSite(sites[0].name);
  }, [industry, sites]);

  const runAnalysis = async () => {
    if (!selectedSite) return;
    setAnalyzing(true);
    setSaved(false);
    try {
      const { data } = await runWhatIf({
        industry,
        site: selectedSite,
        parameter_changes: params,
        question: question || undefined,
      });
      setResult(data);
      setScenarioTitle(`${industry} — ${selectedSite} — ${new Date().toLocaleDateString()}`);
    } catch (err) {
      console.error('What-if analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!result || !scenarioTitle.trim()) return;
    setSaving(true);
    try {
      await saveScenario({
        title: scenarioTitle,
        industry,
        site: selectedSite,
        parameter_changes: params,
        projections: result.projections || {},
        analysis: result.analysis || '',
      });
      setSaved(true);
      setHistoryKey(prev => prev + 1);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const bgImage = BG_IMAGES[industry] || BG_IMAGES['Manufacturing'];

  return (
    <div className="space-y-6" data-testid="digital-twin-panel">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
          Digital Twin Engine
        </h1>
        <p className="font-body text-sm text-text-secondary mt-1">
          Simulate operational changes, predict EESG outcomes, and compare scenarios
        </p>
      </div>

      {/* Visual twin area */}
      <div className="relative rounded-xl overflow-hidden border border-border" style={{ minHeight: 280 }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})`, filter: 'brightness(0.4)' }}
        />
        <div className="relative z-10 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Cube size={20} weight="duotone" className="text-white" />
            <span className="font-heading text-lg font-bold text-white">
              {industry} — Digital Twin Simulation
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Lightning, label: 'Energy', value: `${params[currentParams[0]?.key] || 0}${currentParams[0]?.unit || '%'}` },
              { icon: Leaf, label: 'Emissions', value: 'Live' },
              { icon: Fire, label: 'Efficiency', value: `${params[currentParams[2]?.key] || 0}${currentParams[2]?.unit || '%'}` },
              { icon: Thermometer, label: 'Performance', value: 'Active' },
            ].map((node, i) => (
              <div key={i} className="backdrop-blur-md bg-white/15 border border-white/20 rounded-lg p-3" data-testid={`twin-node-${i}`}>
                <node.icon size={16} className="text-white/80 mb-1" />
                <p className="text-[10px] font-body text-white/60 uppercase tracking-wider">{node.label}</p>
                <p className="font-heading text-sm font-bold text-white">{node.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Parameter controls */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">
            What-If Parameters
          </h3>
          <div className="mb-4">
            <label className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-2 block">Site</label>
            <select
              data-testid="twin-site-select"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg font-body text-sm text-text-primary focus:outline-none focus:border-primary"
            >
              {sites.map(s => <option key={s.name} value={s.name}>{s.name} — {s.type}</option>)}
            </select>
          </div>

          <div className="space-y-4">
            {currentParams.map((param) => (
              <div key={param.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-body font-medium text-text-secondary">{param.label}</label>
                  <span className="text-xs font-heading font-bold text-primary">{params[param.key]}{param.unit}</span>
                </div>
                <input
                  data-testid={`what-if-slider-${param.key}`}
                  type="range"
                  min={param.min}
                  max={param.max}
                  step={param.max > 50 ? 1 : 0.5}
                  value={params[param.key]}
                  onChange={(e) => setParams({ ...params, [param.key]: parseFloat(e.target.value) })}
                  className="w-full h-1.5 bg-border rounded-full appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-[10px] font-body text-tertiary mt-0.5">
                  <span>{param.min}{param.unit}</span>
                  <span>{param.max}{param.unit}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <label className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-2 block">Custom Question (Optional)</label>
            <textarea
              data-testid="twin-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about specific impacts, trade-offs, or optimizations..."
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg font-body text-sm text-text-primary focus:outline-none focus:border-primary resize-none"
              rows={2}
            />
          </div>

          <button
            data-testid="run-analysis-button"
            onClick={runAnalysis}
            disabled={analyzing || !selectedSite}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-lg font-heading font-semibold text-sm hover:bg-[#2B3523] transition-all duration-200 disabled:opacity-50"
          >
            {analyzing ? (
              <><SpinnerGap size={16} className="animate-spin" /> Analyzing with AI...</>
            ) : (
              <>Run Simulation <ArrowRight size={16} weight="bold" /></>
            )}
          </button>
        </div>

        {/* Results panel */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-text-primary">Simulation Results</h3>
            {result && !analyzing && (
              <div className="flex items-center gap-2">
                {saved ? (
                  <span className="flex items-center gap-1 text-xs font-body text-success font-medium" data-testid="scenario-saved-indicator">
                    <CheckCircle size={14} weight="fill" /> Saved
                  </span>
                ) : (
                  <button
                    data-testid="save-scenario-button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary/10 text-secondary rounded-lg text-xs font-body font-medium hover:bg-secondary/20 transition-all disabled:opacity-50"
                  >
                    {saving ? <SpinnerGap size={12} className="animate-spin" /> : <FloppyDisk size={14} />}
                    Save Scenario
                  </button>
                )}
              </div>
            )}
          </div>

          {!result && !analyzing && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Cube size={48} weight="duotone" className="text-border mb-3" />
              <p className="font-body text-sm text-text-secondary">
                Adjust parameters and run simulation to see AI-powered projections
              </p>
            </div>
          )}

          {analyzing && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <SpinnerGap size={40} className="text-primary animate-spin mb-3" />
              <p className="font-body text-sm text-text-secondary">Claude is analyzing your scenario...</p>
            </div>
          )}

          {result && !analyzing && (
            <div className="space-y-4 max-h-[500px] overflow-y-auto" data-testid="simulation-results">
              {/* Save title input */}
              {!saved && (
                <input
                  data-testid="scenario-title-input"
                  value={scenarioTitle}
                  onChange={(e) => setScenarioTitle(e.target.value)}
                  placeholder="Scenario name..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg font-body text-xs text-text-primary focus:outline-none focus:border-primary"
                />
              )}

              {/* Projections */}
              {result.projections && Object.keys(result.projections).length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(result.projections).slice(0, 4).map(([key, val]) => (
                    <div key={key} className="p-3 bg-background rounded-lg border border-border">
                      <p className="text-[10px] font-body text-tertiary uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</p>
                      <div className="flex items-end gap-2">
                        <span className="font-heading text-lg font-bold text-text-primary">{val.projected}</span>
                        <span className={`text-xs font-body font-medium ${val.change_pct < 0 ? 'text-success' : 'text-alert-critical'}`}>
                          {val.change_pct > 0 ? '+' : ''}{val.change_pct}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* AI Analysis */}
              <div className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <ChartLineUp size={16} className="text-primary" />
                  <span className="text-xs font-body font-semibold text-primary uppercase tracking-wider">AI Analysis</span>
                </div>
                <div className="prose prose-sm max-w-none font-body text-text-primary text-sm leading-relaxed">
                  <ReactMarkdown>{result.analysis}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scenario History & Comparison */}
      <ScenarioHistory key={historyKey} industry={industry} />
    </div>
  );
}
